import { Component, ElementRef, ViewEncapsulation } from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { removeAmChartLogo } from 'app/core/shared/amchart4/amchart4.module';
import { Router } from '@angular/router';
import { PmDashboardService } from 'app/modules/well-integrity-planning/services/pm-dashboard.service';
import module from 'app/modules/well-integrity-planning/well-integrity-planning.module';

am4core.useTheme(am4themes_animated);

@Component({
    selector: 'app-preventive-maintainance',
    templateUrl: './pm-dashboard.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./pm-dashboard.page.scss'],
})
export class PMDashboardPage {
    fieldSummary$ = this.pmDashboardService.fieldSummary$;

    pmCompleteness: any;
    activityCountTrending: any;

    constructor(
        private elementRef: ElementRef,
        public router: Router,
        private pmDashboardService: PmDashboardService,
    ) {}

    ngOnInit(): void {
        this.fieldSummary$.subscribe((fieldSummary) => {
            this.pmCompleteness = fieldSummary?.data.pmCompletenesses;
            this.activityCountTrending =
                fieldSummary?.data.activityCountTrending;

            if (this.pmCompleteness) {
                this.createPieChart();
            }

            if (this.activityCountTrending) {
                this.createBarGraph();
            }
        });
    }

    private createPieChart() {
        // Create chart instance
        let chart = am4core.create(
            'pmCompletenesspieChart',
            am4charts.PieChart,
        );
        chart.padding(15, 10, 5, 0);

        // Add data
        chart.data = [
            {
                category: 'Completed',
                value: this.pmCompleteness.completed,
                color: am4core.color('#7ed77e'),
            },
            {
                category: 'Pending',
                value: this.pmCompleteness.pending,
                color: am4core.color('#FFC300'),
            },
            {
                category: 'Overdue',
                value: this.pmCompleteness.overdue,
                color: am4core.color('#FF0000'),
            },
        ];

        // Add and configure Series
        let pieSeries = chart.series.push(new am4charts.PieSeries());
        pieSeries.dataFields.value = 'value';
        pieSeries.dataFields.category = 'category';
        pieSeries.slices.template.stroke = am4core.color('#fff');
        pieSeries.slices.template.strokeWidth = 2;
        pieSeries.slices.template.strokeOpacity = 1;
        pieSeries.slices.template.propertyFields.fill = 'color'; // You can specify a default color

        pieSeries.labels.template.text =
            "{values.value.percent.formatNumber('#.#')}%";
        pieSeries.labels.template.fill = am4core.color('#000');
        pieSeries.alignLabels = false;
        pieSeries.labels.template.padding(0, 0, 0, 0);
        pieSeries.ticks.template.disabled = true;

        // This creates initial animation
        pieSeries.hiddenState.properties.opacity = 1;
        pieSeries.hiddenState.properties.endAngle = -90;
        pieSeries.hiddenState.properties.startAngle = -90;

        // Add a legend
        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.valign = 'bottom';
        chart.legend.contentAlign = 'center';
        chart.legend.labels.template.fontSize = 12;
        chart.legend.labels.template.fill = am4core.color('#000');
        chart.legend.itemContainers.template.paddingTop = 0;
        chart.legend.itemContainers.template.paddingBottom = 0;
        chart.legend.marginTop = 20;
        chart.legend.valueLabels.template.disabled = true;

        // Custom marker style
        chart.legend.markers.template.width = 12;
        chart.legend.markers.template.height = 12;
        let markerTemplate = chart.legend.markers.template.children.getIndex(
            0,
        ) as am4core.RoundedRectangle;
        markerTemplate.cornerRadius(4, 4, 4, 4);

        removeAmChartLogo(this.elementRef.nativeElement);
    }

    private createBarGraph() {
        // Create chart instance
        let chart = am4core.create('barGraph', am4charts.XYChart);

        const monthNames: { [key: string]: string } = {
            january: 'Jan',
            february: 'Feb',
            march: 'Mar',
            april: 'Apr',
            may: 'May',
            june: 'Jun',
            july: 'Jul',
            august: 'Aug',
            september: 'Sep',
            october: 'Oct',
            november: 'Nov',
            december: 'Dec',
        };

        chart.data = this.activityCountTrending.lists.map((item) => ({
            ...item,
            month: monthNames[item.month.toLowerCase().trim()] || item.month,
        }));

        // Create axes
        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'month';
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.minGridDistance = 30;

        categoryAxis.renderer.labels.template.fill = am4core.color('#000');

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.renderer.labels.template.rotation = 0; // Set rotation to 0 for horizontal labels
        valueAxis.title.text = 'Number of String'; // Rename Y-axis
        valueAxis.title.fill = am4core.color('#000');
        valueAxis.renderer.labels.template.fill = am4core.color('#000');

        // Create series
        let series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = 'count';
        series.dataFields.categoryX = 'month';
        series.name = 'Counts';
        series.columns.template.tooltipText = '{categoryX}: [bold]{valueY}[/]';
        series.columns.template.fillOpacity = 0.8;

        let columnTemplate = series.columns.template;
        columnTemplate.strokeWidth = 2;
        columnTemplate.strokeOpacity = 1;

        removeAmChartLogo(this.elementRef.nativeElement);
    }

    toggleSummary(routes: any) {
        this.router.navigate([
            `/wim/well-integrity-planning/pm-dashboard/${routes}`,
        ]);
    }
}
